import React, { ReactNode, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { MIN_PASSWORD_LENGTH, validateNewPassword } from '../lib/authErrors';
import { AuthError, authInputClass, authPrimaryButtonClass } from './AuthLayout';

interface PasswordChangeFormProps {
  /** Performs the change; resolves to a user-facing error message or null on success. */
  onSubmit: (password: string) => Promise<string | null>;
  submitLabel?: string;
  /** Extra actions rendered under the submit button (e.g. a cancel link). */
  secondaryAction?: ReactNode;
  /** When true, the fields are cleared after a successful change. */
  resetOnSuccess?: boolean;
}

/** New password + confirmation fields shared by the recovery page and "Mi cuenta". */
export default function PasswordChangeForm({ onSubmit, submitLabel = 'Guardar contraseña', secondaryAction, resetOnSuccess }: PasswordChangeFormProps) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    const invalid = validateNewPassword(password, confirmation);
    if (invalid) {
      setError(invalid);
      return;
    }
    setIsLoading(true);
    try {
      const failure = await onSubmit(password);
      if (failure) setError(failure);
      else if (resetOnSuccess) {
        setPassword('');
        setConfirmation('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={handleSubmit} noValidate>
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
          Nueva contraseña
        </label>
        <input
          id="newPassword"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          required
          minLength={MIN_PASSWORD_LENGTH}
          aria-describedby="newPasswordHint"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={`mt-1 ${authInputClass}`}
        />
        <p id="newPasswordHint" className="mt-1 text-xs text-slate-500">
          Mínimo {MIN_PASSWORD_LENGTH} caracteres. Combina letras, números y símbolos.
        </p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
          Confirmar contraseña
        </label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          className={`mt-1 ${authInputClass}`}
        />
      </div>

      <AuthError message={error} />

      <div className="space-y-3">
        <button type="submit" disabled={isLoading} className={authPrimaryButtonClass}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {isLoading ? 'Guardando…' : submitLabel}
        </button>
        {secondaryAction}
      </div>
    </form>
  );
}
